import { Card, cn } from '@trafiflow/shared-ui'
import {
  CheckCircle,
  Clock,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  Search,
  Sparkles,
  Upload,
  Video,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useBrandContext } from '../../hooks/useBrandContext'
import CompanyVideoDetailModal from './CompanyVideoDetailModal'
import {
  type CompanyVideoWithThumbnail,
  type ListCompanyVideosParams,
  type UploadProgressPhase,
  deleteCompanyVideo,
  getCompanyVideo,
  listCompanyVideos,
  updateCompanyVideo,
  uploadCompanyVideo,
  reanalyzeCompanyVideo,
} from './companyVideos.service'
import { FootageFilterSidebar, type FootageFilters } from './FootageFilterSidebar'
import { VideoThumbnail } from './VideoThumbnail'

function parseTags(value: string): string[] {
  return value
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
}

function formatBytes(bytes?: number) {
  if (!bytes || bytes <= 0) return ''
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let idx = 0
  let val = bytes
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024
    idx++
  }
  return `${val.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`
}

function formatDuration(sec?: number) {
  if (!sec || sec <= 0) return null
  const mins = Math.floor(sec / 60)
  const secs = Math.floor(sec % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

interface UploadQueueItem {
  id: string
  file: File
  status: 'pending' | 'uploading' | 'uploaded' | 'completed' | 'error'
  progress: number
  error?: string
  videoId?: string
}

export function FootageRepository() {
  const { buildPrompt } = useBrandContext()
  const [videos, setVideos] = useState<CompanyVideoWithThumbnail[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid')
  const [filters, setFilters] = useState<FootageFilters>({ sortBy: 'newest' })

  // Upload dialog
  const [showUpload, setShowUpload] = useState(false)
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([])
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [tags, setTags] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  // Detail modal
  const [selected, setSelected] = useState<CompanyVideoWithThumbnail | null>(null)
  const [selectedDownloadUrl, setSelectedDownloadUrl] = useState<string>('')
  const [selectedCleanUrl, setSelectedCleanUrl] = useState<string>('')
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [reanalyzing, setReanalyzing] = useState(false)

  const apiParams = useMemo((): ListCompanyVideosParams => {
    return {
      limit: 100,
      q: searchQuery.trim() || undefined,
      contentRole: filters.contentRole,
      voice: filters.voice,
      shotSize: filters.shotSize,
      pointOfView: filters.pointOfView,
      quality: filters.quality,
      actionType: filters.actionType,
    }
  }, [searchQuery, filters])

  const sortedVideos = useMemo(() => {
    const sorted = [...videos]
    switch (filters.sortBy) {
      case 'newest':
        sorted.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        break
      case 'oldest':
        sorted.sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())
        break
      case 'alphabetical':
        sorted.sort((a, b) => (a.title || a.fileName).localeCompare(b.title || b.fileName))
        break
    }
    return sorted
  }, [videos, filters.sortBy])

  const loadVideos = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await listCompanyVideos(apiParams)
      setVideos(res.videos || [])
    } catch (err: unknown) {
      console.error('[FootageRepository] Failed to load videos:', err)
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      setError(e.response?.data?.message || e.message || 'Failed to load videos')
    } finally {
      setLoading(false)
    }
  }, [apiParams])

  useEffect(() => {
    loadVideos()
  }, [loadVideos])

  // Upload handlers
  const resetUploadForm = () => {
    setUploadQueue([])
    setTitle('')
    setDescription('')
    setCategory('')
    setTags('')
    setUploadError(null)
  }

  const handleFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return
    const newItems: UploadQueueItem[] = Array.from(files).map((file, index) => ({
      id: `${Date.now()}-${index}`,
      file,
      status: 'pending' as const,
      progress: 0,
    }))
    setUploadQueue((prev) => [...prev, ...newItems])
    if (!title.trim() && newItems.length > 0) {
      setTitle(newItems[0].file.name.replace(/\.[^/.]+$/, ''))
    }
  }

  const removeFromQueue = (id: string) => {
    setUploadQueue((prev) => prev.filter((item) => item.id !== id))
  }

  const handleUpload = async () => {
    if (uploadQueue.length === 0) {
      setUploadError('Pick a video file first.')
      return
    }

    setUploading(true)
    setUploadError(null)

    for (let i = 0; i < uploadQueue.length; i++) {
      const item = uploadQueue[i]
      if (item.status === 'completed') continue

      try {
        const result = await uploadCompanyVideo({
          file: item.file,
          title:
            uploadQueue.length === 1
              ? title.trim() || undefined
              : item.file.name.replace(/\.[^/.]+$/, ''),
          description: description.trim() || undefined,
          category: category.trim() || undefined,
          tags: parseTags(tags),
          onProgress: (phase: UploadProgressPhase, videoId?: string) => {
            const statusMap: Record<UploadProgressPhase, UploadQueueItem['status']> = {
              creating: 'uploading',
              uploading: 'uploading',
              completing: 'uploaded',
              completed: 'completed',
            }
            const progressMap: Record<UploadProgressPhase, number> = {
              creating: 10,
              uploading: 40,
              completing: 80,
              completed: 100,
            }
            setUploadQueue((prev) =>
              prev.map((q) =>
                q.id === item.id
                  ? { ...q, status: statusMap[phase], progress: progressMap[phase], videoId }
                  : q,
              ),
            )
          },
        })

        const video = result as CompanyVideoWithThumbnail
        setUploadQueue((prev) =>
          prev.map((q) =>
            q.id === item.id
              ? { ...q, status: 'completed' as const, progress: 100, videoId: video?.id }
              : q,
          ),
        )

        // Add to video list immediately
        if (video?.id) {
          setVideos((prev) => {
            const exists = prev.some((v) => v.id === video.id)
            return exists ? prev.map((v) => (v.id === video.id ? video : v)) : [video, ...prev]
          })
        }
      } catch (err: unknown) {
        console.error('[FootageRepository] Upload failed:', err)
        const e = err as { response?: { data?: { message?: string } }; message?: string }
        const errorMsg = e.response?.data?.message || e.message || 'Upload failed'
        setUploadQueue((prev) =>
          prev.map((q) =>
            q.id === item.id ? { ...q, status: 'error' as const, error: errorMsg } : q,
          ),
        )
        if (uploadQueue.length === 1) setUploadError(errorMsg)
      }
    }

    // Refresh the full list and clean up completed items
    await loadVideos()
    setUploading(false)

    // Check if all uploads succeeded (no errors)
    const hasErrors = uploadQueue.some((q) => q.status === 'error')

    // Clear completed items and reset form after a short delay
    setTimeout(() => {
      setUploadQueue((prev) => prev.filter((q) => q.status !== 'completed'))
      // Reset form fields if all uploads succeeded
      if (!hasErrors) {
        setTitle('')
        setDescription('')
        setCategory('')
        setTags('')
      }
    }, 3000)
  }

  // Detail handlers
  const openDetails = async (video: CompanyVideoWithThumbnail) => {
    setSelected(video)
    setSelectedDownloadUrl('')
    setSelectedCleanUrl('')
    setDetailError(null)
    setDetailLoading(true)

    try {
      const res = await getCompanyVideo(video.id)
      setSelected(res.video)
      setSelectedDownloadUrl(res.downloadUrl || '')
      setSelectedCleanUrl(res.cleanVersionUrl || '')
    } catch (err: unknown) {
      console.error('[FootageRepository] Failed to load video details:', err)
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      setDetailError(e.response?.data?.message || e.message || 'Failed to load video')
    } finally {
      setDetailLoading(false)
    }
  }

  const closeDetails = () => {
    setSelected(null)
    setSelectedDownloadUrl('')
    setSelectedCleanUrl('')
    setDetailError(null)
    setDetailLoading(false)
    setSaving(false)
    setDeleting(false)
    setReanalyzing(false)
  }

  const handleSave = async (input: {
    title: string
    description: string
    category: string
    tags: string[]
  }) => {
    if (!selected) return
    setSaving(true)
    setDetailError(null)

    try {
      const updated = await updateCompanyVideo(selected.id, {
        title: input.title,
        description: input.description || undefined,
        category: input.category || undefined,
        tags: input.tags,
      })
      setSelected(updated)
      setVideos((prev) => prev.map((v) => (v.id === updated.id ? updated : v)))
    } catch (err: unknown) {
      console.error('[FootageRepository] Failed to save video:', err)
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      setDetailError(e.response?.data?.message || e.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selected) return
    setDeleting(true)
    setDetailError(null)

    try {
      await deleteCompanyVideo(selected.id)
      closeDetails()
      await loadVideos()
    } catch (err: unknown) {
      console.error('[FootageRepository] Failed to delete video:', err)
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      setDetailError(e.response?.data?.message || e.message || 'Failed to delete')
      setDeleting(false)
    }
  }

  const handleReanalyze = async () => {
    if (!selected) return
    setDetailError(null)
    setReanalyzing(true)
    try {
      const brandContext = buildPrompt()
      const updated = await reanalyzeCompanyVideo(selected.id, { brandContext })
      setSelected(updated)
      setVideos((prev) => prev.map((v) => (v.id === updated.id ? updated : v)))
    } catch (err: unknown) {
      console.error('[FootageRepository] Failed to re-analyze:', err)
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      setDetailError(e.response?.data?.message || e.message || 'Failed to trigger re-analysis')
    } finally {
      setReanalyzing(false)
    }
  }

  // Poll selected video metadata while AI analysis or clean is in progress.
  // Only updates metadata fields — never touches download/clean URLs to avoid
  // remounting the <video> element mid-playback.
  useEffect(() => {
    if (!selected) return
    const needsPoll =
      selected.aiAnalysisStatus === 'pending' ||
      selected.aiAnalysisStatus === 'processing' ||
      selected.cleanStatus === 'pending' ||
      selected.cleanStatus === 'processing'
    if (!needsPoll) return

    const interval = setInterval(async () => {
      try {
        const res = await getCompanyVideo(selected.id)
        setSelected(res.video)
        setVideos((prev) => prev.map((v) => (v.id === res.video.id ? res.video : v)))

        // Update download URL only on first load to avoid video remount
        setSelectedDownloadUrl((prev) => prev || res.downloadUrl || '')
        // Always update clean URL when it becomes available (won't affect video since toggle controls source)
        if (res.cleanVersionUrl) {
          setSelectedCleanUrl(res.cleanVersionUrl)
        }

        const done =
          res.video.aiAnalysisStatus !== 'pending' &&
          res.video.aiAnalysisStatus !== 'processing' &&
          res.video.cleanStatus !== 'pending' &&
          res.video.cleanStatus !== 'processing'
        if (done) clearInterval(interval)
      } catch {
        // silently ignore poll errors
      }
    }, 5000)

    return () => clearInterval(interval)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, selected?.aiAnalysisStatus, selected?.cleanStatus])

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') loadVideos()
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-tf-border">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
            <Video className="w-4 h-4 text-blue-600" />
          </div>
          <h1 className="text-lg font-semibold text-tf-text">Footage Repository</h1>
        </div>
        <button
          type="button"
          onClick={() => setShowUpload(!showUpload)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>

      {/* Upload dialog */}
      {showUpload && (
        <div className="px-6 py-4 border-b border-tf-border bg-tf-bg-muted">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Upload className="w-4 h-4 text-blue-600" />
              <h2 className="text-sm font-semibold text-tf-text">Ingest video</h2>
            </div>
            <button
              type="button"
              onClick={() => {
                if (!uploading) {
                  setShowUpload(false)
                  resetUploadForm()
                }
              }}
              disabled={uploading}
              className="p-1 rounded hover:bg-tf-bg-hover disabled:opacity-30"
            >
              <X className="w-4 h-4 text-tf-text-muted" />
            </button>
          </div>

          {/* Form fields — hidden while uploading */}
          {!uploading && uploadQueue.every((q) => q.status === 'pending') && (
            <>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-tf-text-muted">Video files</label>
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={(e) => handleFilesSelected(e.target.files)}
                    className="w-full rounded border border-tf-border bg-tf-bg-card px-3 py-1.5 text-sm text-tf-text"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-tf-text-muted">Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded border border-tf-border bg-tf-bg-card px-3 py-1.5 text-sm text-tf-text focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="e.g. US - Valentine UGC - v2"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-tf-text-muted">Category</label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full rounded border border-tf-border bg-tf-bg-card px-3 py-1.5 text-sm text-tf-text focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="e.g. ugc, product-demo"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-tf-text-muted">Tags</label>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    className="w-full rounded border border-tf-border bg-tf-bg-card px-3 py-1.5 text-sm text-tf-text focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="comma-separated"
                  />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-xs font-medium text-tf-text-muted">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full min-h-[60px] rounded border border-tf-border bg-tf-bg-card px-3 py-1.5 text-sm text-tf-text focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Optional context..."
                  />
                </div>
              </div>

              {/* Pending file list */}
              {uploadQueue.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {uploadQueue.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 px-3 py-2 bg-tf-bg-card rounded-lg border border-tf-border"
                    >
                      <Video className="w-4 h-4 text-tf-text-muted flex-shrink-0" />
                      <span className="flex-1 truncate text-sm font-medium text-tf-text">
                        {item.file.name}
                      </span>
                      <span className="text-xs text-tf-text-muted">{formatBytes(item.file.size)}</span>
                      <button
                        type="button"
                        onClick={() => removeFromQueue(item.id)}
                        className="p-0.5 hover:bg-tf-bg-hover rounded"
                      >
                        <X className="w-3.5 h-3.5 text-tf-text-muted" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Progress tracker — shown during upload/analysis */}
          {(uploading || uploadQueue.some((q) => q.status !== 'pending')) && (
            <div className="space-y-3">
              {uploadQueue.map((item) => (
                <div
                  key={item.id}
                  className="rounded-lg border border-tf-border bg-tf-bg-card overflow-hidden"
                >
                  <div className="flex items-center gap-3 px-4 py-3">
                    <Video className="w-4 h-4 text-tf-text-muted flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-tf-text truncate">{item.file.name}</p>
                      <p className="text-[11px] text-tf-text-muted mt-0.5">
                        {formatBytes(item.file.size)}
                      </p>
                    </div>
                    {item.status === 'completed' && (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    )}
                    {item.status === 'error' && (
                      <span className="text-xs font-medium text-red-600">Failed</span>
                    )}
                  </div>

                  {/* Step indicators */}
                  <div className="px-4 pb-3">
                    <div className="flex items-center gap-2">
                      {/* Step 1: Upload */}
                      <div className="flex items-center gap-1.5 flex-1">
                        <div className={cn(
                          'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold',
                          item.status === 'pending' ? 'bg-gray-200 text-gray-500' :
                            item.status === 'uploading' ? 'bg-blue-100 text-blue-600' :
                              'bg-green-100 text-green-600',
                        )}>
                          {item.status === 'uploading' ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : item.status !== 'pending' ? (
                            <CheckCircle className="w-3 h-3" />
                          ) : '1'}
                        </div>
                        <span className={cn(
                          'text-[11px] font-medium',
                          item.status === 'uploading' ? 'text-blue-600' :
                            item.status === 'pending' ? 'text-tf-text-muted' :
                              'text-green-600',
                        )}>
                          {item.status === 'uploading' ? 'Uploading...' :
                            item.status === 'pending' ? 'Upload' : 'Uploaded'}
                        </span>
                      </div>

                      <div className="w-6 h-px bg-gray-300 flex-shrink-0" />

                      {/* Step 2: AI Analysis (runs async after upload) */}
                      <div className="flex items-center gap-1.5 flex-1">
                        <div className={cn(
                          'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold',
                          item.status === 'completed' ? 'bg-violet-100 text-violet-600' :
                            'bg-gray-200 text-gray-500',
                        )}>
                          {item.status === 'completed' ? (
                            <Clock className="w-3 h-3" />
                          ) : (
                            <Sparkles className="w-3 h-3" />
                          )}
                        </div>
                        <span className={cn(
                          'text-[11px] font-medium',
                          item.status === 'completed' ? 'text-violet-600' :
                            'text-tf-text-muted',
                        )}>
                          {item.status === 'completed' ? 'AI Queued' : 'AI Analysis'}
                        </span>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all duration-700',
                          item.status === 'error' ? 'bg-red-500' :
                            item.status === 'completed' ? 'bg-green-500' :
                              'bg-blue-500',
                        )}
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>

                    {item.error && (
                      <p className="text-[11px] text-red-600 mt-1.5">{item.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {uploadError && !uploading && (
            <div className="mt-3 rounded border border-red-200 bg-tf-feedback-error-bg px-3 py-2">
              <p className="text-xs text-red-700">{uploadError}</p>
            </div>
          )}

          <div className="flex gap-2 mt-4">
            {!uploading && uploadQueue.every((q) => q.status === 'pending') && (
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploadQueue.length === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="w-4 h-4" />
                Upload{uploadQueue.length > 1 ? ` (${uploadQueue.length})` : ''}
              </button>
            )}
            {!uploading && uploadQueue.some((q) => q.status !== 'pending') && (
              <button
                type="button"
                onClick={() => {
                  resetUploadForm()
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Upload another
              </button>
            )}
            {!uploading && uploadQueue.every((q) => q.status === 'pending') && (
              <button
                type="button"
                onClick={resetUploadForm}
                className="px-4 py-2 rounded-lg border border-tf-border text-sm text-tf-text-sub hover:bg-tf-bg-muted transition-colors"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main area: sidebar + content */}
      <div className="flex flex-1 overflow-hidden">
        <FootageFilterSidebar filters={filters} onChange={setFilters} />

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-tf-border">
            <span className="text-sm text-tf-text-muted">
              <span className="font-semibold text-tf-text">{videos.length}</span> files
            </span>

            <div className="flex-1" />

            {/* View toggle */}
            <div className="flex border border-tf-border rounded overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={cn(
                  'p-1.5 transition-colors',
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : 'bg-tf-bg-card text-tf-text-sub hover:bg-tf-bg-muted',
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={cn(
                  'p-1.5 transition-colors',
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'bg-tf-bg-card text-tf-text-sub hover:bg-tf-bg-muted',
                )}
                aria-label="List view"
              >
                <List className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Search */}
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-tf-text-muted" />
              <input
                type="text"
                placeholder="Search by name, description or transcript"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="w-full pl-8 pr-3 py-1.5 border border-tf-border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading && (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                <span className="ml-2 text-tf-text-muted">Loading video library...</span>
              </div>
            )}

            {!loading && error && (
              <div className="rounded-lg border border-red-200 bg-tf-feedback-error-bg px-4 py-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {!loading && !error && sortedVideos.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-14 h-14 bg-tf-bg-hover rounded-full flex items-center justify-center mb-4">
                  <Video className="w-7 h-7 text-tf-text-muted" />
                </div>
                <h3 className="text-base font-semibold text-tf-text">No videos found</h3>
                <p className="text-sm text-tf-text-muted mt-1">
                  Upload footage or adjust your filters.
                </p>
              </div>
            )}

            {!loading && !error && sortedVideos.length > 0 && viewMode === 'grid' && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {sortedVideos.map((v) => (
                  <div
                    key={v.id}
                    className="group relative rounded-xl overflow-hidden bg-[#111] cursor-pointer hover:ring-2 hover:ring-blue-500/50 transition-all"
                    onClick={() => openDetails(v)}
                  >
                    <div className="aspect-[9/16] relative">
                      <VideoThumbnail
                        videoId={v.id}
                        videoStatus={v.status}
                        thumbnailUrl={v.thumbnailUrl}
                        className="absolute inset-0"
                      />

                      {/* Title overlay at top */}
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent px-3 pt-2.5 pb-6">
                        <div className="flex items-start justify-between gap-1">
                          <p className="text-xs font-semibold text-white truncate leading-tight drop-shadow-sm">
                            {v.title || v.fileName}
                          </p>
                          {v.aiAnalysisStatus === 'ready' && (
                            <Sparkles className="w-3.5 h-3.5 text-violet-300 flex-shrink-0 mt-0.5" />
                          )}
                        </div>
                      </div>

                      {/* Bottom bar */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-3 pb-2.5 pt-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            {formatDuration(v.durationSec) && (
                              <span className="text-[11px] text-white font-medium tabular-nums drop-shadow-sm">
                                {formatDuration(v.durationSec)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5">
                            {v.cleanStatus === 'ready' && (
                              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-500/90 text-white">
                                Clean
                              </span>
                            )}
                            {(v.cleanStatus === 'pending' || v.cleanStatus === 'processing') && (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-300" />
                            )}
                            {(v.aiAnalysisStatus === 'pending' ||
                              v.aiAnalysisStatus === 'processing') && (
                                <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-300" />
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && !error && sortedVideos.length > 0 && viewMode === 'list' && (
              <Card>
                <div className="divide-y divide-tf-border">
                  {sortedVideos.map((v) => (
                    <div
                      key={v.id}
                      className="p-3 hover:bg-tf-bg-muted transition-colors cursor-pointer flex items-center gap-4"
                      onClick={() => openDetails(v)}
                    >
                      <div className="w-16 h-10 flex-shrink-0 rounded bg-tf-bg-muted flex items-center justify-center">
                        <Video className="w-5 h-5 text-tf-text-muted" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-tf-text text-sm truncate">
                            {v.title || v.fileName}
                          </p>
                          {v.aiAnalysisStatus === 'ready' && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
                              AI
                            </span>
                          )}
                          {v.cleanStatus === 'ready' && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">
                              <CheckCircle className="w-2.5 h-2.5" />
                              Clean
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-xs text-tf-text-muted truncate">{v.fileName}</p>
                          {v.contentRole && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                              {v.contentRole.replace(/_/g, ' ')}
                            </span>
                          )}
                          {formatDuration(v.durationSec) && (
                            <span className="text-[10px] text-tf-text-muted">
                              {formatDuration(v.durationSec)}
                            </span>
                          )}
                          {v.sizeBytes ? (
                            <span className="text-[10px] text-tf-text-muted">
                              {formatBytes(v.sizeBytes)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Detail modal */}
      {selected && (
        <CompanyVideoDetailModal
          video={selected}
          downloadUrl={selectedDownloadUrl}
          cleanVersionUrl={selectedCleanUrl}
          loading={detailLoading}
          error={detailError}
          saving={saving}
          deleting={deleting}
          reanalyzing={reanalyzing}
          onClose={closeDetails}
          onSave={handleSave}
          onDelete={handleDelete}
          onReanalyze={handleReanalyze}
        />
      )}
    </div>
  )
}

export default FootageRepository
